import { type FactiiiRequirement } from '@shared/all/domains/factiiis';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUserState } from '../context/UserContext';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

const KEY = ['users', 'pinnedFactiiis'] as const;

const usePinnedFactiiis = (client: WrappedTRPCClient) => {
  const { state } = useUserState();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const [, d] = await client.users.pinnedFactiiis.query();
      return (d?.pinnedFactiiis ?? []).map((pf) => ({
        ...pf,
        requirements: pf.requirements as FactiiiRequirement[]
      }));
    },
    enabled: state.user.isAuthenticated === true,
    staleTime: Infinity
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: KEY });
  // Optimistic: the modal reorders by drag, so the list must follow the drop
  // at once. The server rejects anything but a permutation of the current
  // pins, and a rejection refetches so the list snaps back.
  const reorder = async (factiiiIds: number[]) => {
    const previous = data ?? [];
    const byId = new Map(previous.map((pf) => [pf.id, pf]));
    queryClient.setQueryData(
      KEY,
      factiiiIds.flatMap((id) => byId.get(id) ?? [])
    );
    const [error] = await client.factiiis.reorderPins.mutate({ factiiiIds });
    if (error) await invalidate();
  };
  return { pinnedFactiiis: data ?? [], invalidate, reorder };
};

export default usePinnedFactiiis;
